import InvoiceCalculator from '../js/services/invoiceCalculator';
import moment from 'moment';

export default {
  namespaced: true,

  state: {
    calculatedLines: {
      firstInvoice: [],
      followUpInvoice: []
    },
    totals: {
      firstInvoice: 0,
      followUpInvoice: 0
    }
  },

  mutations: {
    SET_CALCULATED_LINES(state, { type, lines }) {
      state.calculatedLines[type] = lines;
    },
    SET_TOTALS(state, { type, total }) {
      state.totals[type] = total;
    }
  },

  actions: {
    calculateInvoice({ commit }, { contract, boxes, products }) {
      const currentPeriodLines = [];
      const startDate = moment(contract.start_date);
      const isAfter15 = startDate.date() >= 15;
      const frequency = contract.invoice_freq || 'month';
      const isBusinessCustomer = contract.customer.type === 'business';
      const isFirstInvoice = !contract.is_follow_up;
      const daysInMonth = startDate.daysInMonth();
      const remainingDays = startDate.clone().endOf('month').diff(startDate, 'days') + 1;

      console.log('Debug - Invoice Calculation Input:', {
        isFirstInvoice,
        isAfter15,
        frequency,
        remainingDays,
        daysInMonth,
        boxes: boxes?.length,
        products: products?.length,
        isBusinessCustomer,
        startDate: startDate.format('YYYY-MM-DD')
      });

      if (isFirstInvoice) {
        // 1. Add box deposits first
        if (boxes?.length) {
          boxes.forEach(box => {
            if (box.deposit > 0) {
              currentPeriodLines.push({
                name: `Borg Box ${box.number}`,
                price: box.deposit,
                btw: 0,
                btwAmount: 0,
                amount: 1,
                total: parseFloat(box.deposit)
              });
            }

            // Add box for remaining days
            const basePrice = parseFloat(box.price);
            const daysPrice = parseFloat(((basePrice / daysInMonth) * remainingDays).toFixed(2));
            const daysBTW = isBusinessCustomer ? parseFloat((daysPrice * (box.btw / 100)).toFixed(2)) : 0;

            // First add the remaining days
            currentPeriodLines.push({
              name: `Box ${box.number} (${remainingDays} ${remainingDays === 1 ? 'dag' : 'dagen'})`,
              price: daysPrice,
              btw: box.btw,
              btwAmount: daysBTW,
              amount: 1,
              total: parseFloat((isBusinessCustomer ? daysPrice + daysBTW : daysPrice).toFixed(2))
            });

            // Then add additional months if needed
            if (!isAfter15 && (frequency === 'quarter' || frequency === 'halfyear' || frequency === 'year')) {
              const additionalMonths = frequency === 'quarter' ? 2 : frequency === 'halfyear' ? 5 : 11;
              const monthsPrice = basePrice;
              const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (box.btw / 100)).toFixed(2)) : 0;
              const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

              currentPeriodLines.push({
                name: `Box ${box.number} (${additionalMonths} ${periodText})`,
                price: monthsPrice,
                btw: box.btw,
                btwAmount: monthsBTW,
                amount: additionalMonths,
                total: parseFloat((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice).toFixed(2)) * additionalMonths
              });
            } else if (isAfter15) {
              const additionalMonths = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1;
              const monthsPrice = basePrice;
              const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (box.btw / 100)).toFixed(2)) : 0;
              const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

              currentPeriodLines.push({
                name: `Box ${box.number} (${additionalMonths} ${periodText})`,
                price: monthsPrice,
                btw: box.btw,
                btwAmount: monthsBTW,
                amount: additionalMonths,
                total: parseFloat((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice).toFixed(2)) * additionalMonths
              });
            }
          });
        }

        // 2. Add product deposits and one-time products
        if (products?.length) {
          products.forEach(product => {
            // Add deposit if any
            if (product.deposit > 0) {
              currentPeriodLines.push({
                name: `Borg ${product.name}`,
                price: product.deposit,
                btw: 0,
                btwAmount: 0,
                amount: product.amount || 1,
                total: parseFloat(((product.deposit) * (product.amount || 1)).toFixed(2))
              });
            }

            // For one-time products, add full price
            if (product.once) {
              const basePrice = parseFloat(product.price);
              const productBTW = isBusinessCustomer ? parseFloat((basePrice * (product.btw / 100)).toFixed(2)) : 0;

              currentPeriodLines.push({
                name: `${product.name} (eenmalig)`,
                price: basePrice,
                btw: product.btw,
                btwAmount: productBTW,
                amount: product.amount || 1,
                total: parseFloat(((isBusinessCustomer ? basePrice + productBTW : basePrice) * (product.amount || 1)).toFixed(2))
              });
            } else {
              // For regular products, calculate remaining days
              const basePrice = parseFloat(product.price);
              const daysPrice = parseFloat(((basePrice / daysInMonth) * remainingDays).toFixed(2));
              const daysBTW = isBusinessCustomer ? parseFloat((daysPrice * (product.btw / 100)).toFixed(2)) : 0;
              const productAmount = product.amount || 1;

              // First add the remaining days
              currentPeriodLines.push({
                name: `${product.name} (${remainingDays} ${remainingDays === 1 ? 'dag' : 'dagen'})`,
                price: daysPrice,
                btw: product.btw,
                btwAmount: daysBTW,
                amount: productAmount,
                total: parseFloat(((isBusinessCustomer ? daysPrice + daysBTW : daysPrice) * productAmount).toFixed(2))
              });

              // Then add additional months if needed
              if (!isAfter15 && (frequency === 'quarter' || frequency === 'halfyear' || frequency === 'year')) {
                const additionalMonths = frequency === 'quarter' ? 2 : frequency === 'halfyear' ? 5 : 11;
                const monthsPrice = basePrice;
                const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (product.btw / 100)).toFixed(2)) : 0;
                const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

                currentPeriodLines.push({
                  name: `${product.name} (${additionalMonths} ${periodText})`,
                  price: monthsPrice,
                  btw: product.btw,
                  btwAmount: monthsBTW,
                  amount: additionalMonths * productAmount,
                  total: parseFloat(((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice) * productAmount * additionalMonths).toFixed(2))
                });
              } else if (isAfter15) {
                const additionalMonths = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1;
                const monthsPrice = basePrice;
                const monthsBTW = isBusinessCustomer ? parseFloat((monthsPrice * (product.btw / 100)).toFixed(2)) : 0;
                const periodText = additionalMonths === 1 ? 'maand' : 'maanden';

                currentPeriodLines.push({
                  name: `${product.name} (${additionalMonths} ${periodText})`,
                  price: monthsPrice,
                  btw: product.btw,
                  btwAmount: monthsBTW,
                  amount: additionalMonths * productAmount,
                  total: parseFloat(((isBusinessCustomer ? monthsPrice + monthsBTW : monthsPrice) * productAmount * additionalMonths).toFixed(2))
                });
              }
            }
          });
        }
      } else {
        // Follow-up invoice calculations
        const periodMonths = frequency === 'quarter' ? 3 : frequency === 'halfyear' ? 6 : frequency === 'year' ? 12 : 1;

        // Add boxes for the full period
        if (boxes?.length) {
          boxes.forEach(box => {
            const basePrice = parseFloat(box.price);
            const periodPrice = parseFloat((basePrice * periodMonths).toFixed(2));
            const periodBTW = isBusinessCustomer ? parseFloat((periodPrice * (box.btw / 100)).toFixed(2)) : 0;

            currentPeriodLines.push({
              name: `Box ${box.number} (${periodMonths} ${periodMonths === 1 ? 'maand' : 'maanden'})`,
              price: basePrice,
              btw: box.btw,
              btwAmount: periodBTW,
              amount: periodMonths,
              total: isBusinessCustomer ? periodPrice + periodBTW : periodPrice
            });
          });
        }

        // Add regular (non-one-time) products for the full period
        if (products?.length) {
          products.filter(p => !p.once).forEach(product => {
            const basePrice = parseFloat(product.price);
            const periodPrice = parseFloat((basePrice * periodMonths).toFixed(2));
            const periodBTW = isBusinessCustomer ? parseFloat((periodPrice * (product.btw / 100)).toFixed(2)) : 0;

            currentPeriodLines.push({
              name: `${product.name} (${periodMonths} ${periodMonths === 1 ? 'maand' : 'maanden'})`,
              price: basePrice,
              btw: product.btw,
              btwAmount: periodBTW,
              amount: periodMonths * (product.amount || 1),
              total: parseFloat(((isBusinessCustomer ? periodPrice + periodBTW : periodPrice) * (product.amount || 1)).toFixed(2))
            });
          });
        }
      }

      console.log(`🔥 Final invoice lines (${contract.is_follow_up ? 'follow-up' : 'first'})`, currentPeriodLines);

      // Calculate totals
      const totals = {
        firstInvoice: currentPeriodLines.reduce((sum, line) => {
          const lineTotal = parseFloat(line?.total || 0);
          return parseFloat((sum + lineTotal).toFixed(2));
        }, 0),
        followUpInvoice: 0
      };

      // Update state based on invoice type
      if (contract.is_follow_up) {
        commit('SET_CALCULATED_LINES', {
          type: 'followUpInvoice',
          lines: currentPeriodLines
        });
        commit('SET_TOTALS', {
          type: 'followUpInvoice',
          total: totals.firstInvoice
        });
      } else {
        commit('SET_CALCULATED_LINES', {
          type: 'firstInvoice',
          lines: currentPeriodLines
        });
        commit('SET_TOTALS', {
          type: 'firstInvoice',
          total: totals.firstInvoice
        });
      }

      return currentPeriodLines;
    }
  },

  getters: {
    grandTotal: state => {
      return parseFloat((state.totals.firstInvoice + state.totals.followUpInvoice).toFixed(2));
    }
  }
};